import React, { Suspense, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { Global404, Logger, Route404 } from '@saviynt/common';
import {
  AlertBannerProvider,
  Loader,
  StackingAlertBanner,
} from '@saviynt/design-system';
import cx from 'classnames';

import Details from '../../pages/Details';
import Recommendation from '../../pages/Recommendation';
import Report from '../../pages/Report';
import Violations from '../../pages/Violations';
import {
  AAG_ACTIVE_TABS,
  AAG_SOD_DETAILS_WITH_ID,
  AAG_SOD_RECOMMENDATION,
  AAG_SOD_REPORT,
  BASENAME,
  SYSTEM_DATA_THEMES,
} from '../../shared/constants';
import { useTypedSelector } from '../../shared/hooks';
import { useLazyGetPersonalizationQuery } from '../../shared/store/globalApis';
import { globalActions } from '../../shared/store/globalSlice';
import {
  hideHostAppElements,
  hideHostAppElementsCleanup,
  removeDynamicBodyClasses,
} from '../../shared/utils';

interface AllowedRoutes {
  staticRoutes: string[];
  dynamicRoutes: string[];
  blockedUrlList: string[];
  blockedNames: string[];
}

/**
 * Routes for AAG
 * @function
 */
function Routes() {
  const dispatch = useDispatch();

  const {
    user: {
      newAAGconfig,
      isMenuPinned,
      headerMenu: { ecmDateFormat },
    },
    aag: {
      global: { alertBanner, hasAAGAccess },
    },
  } = useTypedSelector((state) => state);

  const allowedRoutes: AllowedRoutes = {
    staticRoutes: [],
    dynamicRoutes: [],
    blockedUrlList: [],
    blockedNames: [],
  };

  if (newAAGconfig === '1') {
    allowedRoutes.staticRoutes = [
      ...AAG_ACTIVE_TABS,
      AAG_SOD_DETAILS_WITH_ID,
      AAG_SOD_RECOMMENDATION,
      AAG_SOD_REPORT,
    ];
  }

  useEffect(() => {
    dispatch(globalActions.setGlobalDateFormat(ecmDateFormat));
  }, [ecmDateFormat, dispatch]);

  const [getPersonalization, { isLoading: isPersonalizationLoading }] =
    useLazyGetPersonalizationQuery();

  useEffect(() => {
    // Start the personalization check
    getPersonalization()
      .then((response) => {
        if (response.data) {
          const hasAccess = response.data.newAAGExperience;

          dispatch(globalActions.setHasAAGAccess(hasAccess));
        }
      })
      .catch((error) => {
        Logger.log('Error fetching personalization data:', error);
      });
  }, [dispatch, getPersonalization]);

  useEffect(() => {
    (hideHostAppElements as () => void)();
    (removeDynamicBodyClasses as () => void)();

    return () => (hideHostAppElementsCleanup as () => void)();
  }, []);

  const alertWrapperClasses = cx(
    'Sod-AlertBannerProvider',
    isMenuPinned && 'Sod-AlertBannerProvider--isMenuPinned'
  );

  if (newAAGconfig === '0') {
    allowedRoutes.staticRoutes = ['ECM/soddetail/sodmapped'];

    if (window.location.pathname === '/ECMv6/soddetail/violations') {
      window.location.pathname = '/ECM/soddetail/sodmapped';

      return null;
    }
  }

  // Show loading state while checking personalization
  if (isPersonalizationLoading || hasAAGAccess === null) {
    return (
      <div style={{ position: 'absolute', top: '50%', left: '50%' }}>
        <Loader kind='loop' format='main' color='brand' />
      </div>
    );
  }

  // Show 404 page if access is denied
  if (!hasAAGAccess) {
    return (
      <Suspense key='Aag404' fallback={<div>Loading...</div>}>
        <Router basename={BASENAME}>
          <Global404 />
        </Router>
      </Suspense>
    );
  }

  // Only show the main app if access is explicitly granted
  return (
    <Suspense key='Aag' fallback={<div>Loading...</div>}>
      <Router basename={BASENAME}>
        {/* @ts-expect-error - TODO: Sagar/Shaurabh/Jake ptal */}
        <Route404 routes={allowedRoutes} render={() => <Global404 />}>
          <Switch>
            <AlertBannerProvider
              dataTheme={SYSTEM_DATA_THEMES.LIGHT}
              className={alertWrapperClasses}>
              <Route exact path={AAG_ACTIVE_TABS} component={Violations} />
              <Route exact path={AAG_SOD_DETAILS_WITH_ID} component={Details} />
              <Route
                exact
                path={AAG_SOD_RECOMMENDATION}
                component={Recommendation}
              />
              <Route exact path={AAG_SOD_REPORT} component={Report} />
              <StackingAlertBanner
                colorTheme={alertBanner.kind}
                title={alertBanner.title}
                description={alertBanner.description}
                shouldAutoDismiss
                isVisible={alertBanner.isOpen}
                dataTestId='sod-alert-banner'
                onCancel={() => {
                  dispatch(
                    globalActions.setAlertBanner({
                      ...alertBanner,
                      isOpen: false,
                    })
                  );
                }}
              />
            </AlertBannerProvider>
          </Switch>
        </Route404>
      </Router>
    </Suspense>
  );
}

export default Routes;
